// Dropdown de cuenta anclado al avatar del header.
//
// Comportamiento:
//   - Clic en avatar → abre/cierra el dropdown.
//   - Clic fuera del contenedor o tecla Escape → cierra.
//   - El item "Cerrar sesión del dashboard" ejecuta el local sign-out
//     definido en auth-provider.js (no toca la sesión global Microsoft 365).
//
// Accesibilidad:
//   - El avatar es <button> con aria-haspopup="menu" y aria-expanded.
//   - El dropdown tiene role="menu" y los items role="menuitem".

import { html } from 'htm/preact'
import { useEffect, useRef, useState } from 'preact/hooks'

import { logout } from '../auth/auth-provider.js'

function computeInitials(email) {
  if (!email) return '--'
  const localPart = String(email).split('@')[0] || ''
  const tokens = localPart.split(/[._\-+]/).filter(Boolean)
  let initials
  if (tokens.length >= 2) {
    initials = tokens[0].charAt(0) + tokens[1].charAt(0)
  } else if (tokens.length === 1) {
    initials = tokens[0].slice(0, 2)
  } else {
    initials = '--'
  }
  return initials.toUpperCase()
}

const LOGOUT_ICON = html`
  <svg
    class="account-menu__item-icon"
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
`

export function AccountMenu({ user }) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef(null)

  const email = user?.username || ''
  const displayName = user?.name || (email ? email.split('@')[0] : 'Usuario')
  const initials = computeInitials(email)

  useEffect(() => {
    if (!open) return

    const onPointerDown = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setOpen(false)
      }
    }
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false)
    }

    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const handleLogout = () => {
    setOpen(false)
    logout()
  }

  const toggle = () => setOpen((value) => !value)

  return html`
    <div class="account-menu" ref=${containerRef}>
      <button
        type="button"
        class="header__avatar header__avatar--button"
        aria-haspopup="menu"
        aria-expanded=${open ? 'true' : 'false'}
        title=${`Abre menú de cuenta\n${email}`}
        onClick=${toggle}
      >
        ${initials}
      </button>
      ${open
        ? html`
            <div class="account-menu__dropdown" role="menu" aria-label="Menú de cuenta">
              <div class="account-menu__header">
                <div class="account-menu__name">${displayName}</div>
                <div class="account-menu__email">${email}</div>
              </div>
              <button
                type="button"
                class="account-menu__item"
                role="menuitem"
                onClick=${handleLogout}
              >
                ${LOGOUT_ICON}
                <span>Cerrar sesión del dashboard</span>
              </button>
              <div class="account-menu__hint">
                No afecta tu sesión global de Microsoft 365.
              </div>
            </div>
          `
        : null}
    </div>
  `
}
